import React, { useState, useRef } from "react";
import {
	Typography,
	Divider,
	Table
} from "@material-ui/core";
import ScaleEntryModal from "./ScaleEntryModal";
import Linkify from "react-linkify";
import { Slide } from "react-slideshow-image";
import "./RecipeIngredientsSection.scss";
import "react-slideshow-image/dist/styles.css"

export function RecipeIngredientsSection(props) {
	const defaultImageSize = 300;
	const [isScaleModalOpen, setIsScaleModalOpen] = useState(false);
	const [checkedRows, setCheckedRows] = useState({});
	const imageSize = defaultImageSize;
	const [currentSlide, setCurrentSlide] = useState(0);
	const slideRef = useRef(null);
	const [isFullScreen, setIsFullScreen] = useState(false);
	const [fullScreenIndex, setFullScreenIndex] = useState(0);

	function onRowClick(e, key) {
		const newCheckedRows = { ...checkedRows };

		// Reverse the checked state of the selected row
		if (newCheckedRows[key]) {
			delete newCheckedRows[key];
		}
		else {
			newCheckedRows[key] = true;
		}

		setCheckedRows(newCheckedRows);

		e.preventDefault();
		return false;
	}
	const ingredientsList = props.ingredientsList || [];

	// Helper function to format amount (handle both string and object formats)
	const formatAmount = (amount) => {
		if (!amount) return '';
		if (typeof amount === 'string') return amount;
		if (typeof amount === 'object') {
			// If amount is an object with isDecimal, isFraction, isEmpty properties
			// it might be a flags object, not the actual amount text
			// Return empty string or check for a text property
			return amount.text || amount.value || '';
		}
		return String(amount);
	};

	let tableRows = ingredientsList.map((item, key) => {
		if (item.isHeading) {
			return (<tr key={key}><td colSpan="2" className="rb-recipe-ingredient-list-heading">{item.name}</td></tr>);
		}
		else {
			return (<tr key={key} onClick={(e) => onRowClick(e, key)} className={checkedRows[key] ? "checked-row" : "unchecked-row"}><td>{formatAmount(item.amount)}</td><td>{item.name}</td></tr>);
		}
	});

	let scale = props.scale;

	if (!scale)
		scale = "1";

	let scaleClassName = "scale-label";
	if (scale !== "1")
		scaleClassName += " scale-label-changed";

	let systemClassName = "scale-label";
	if (props.system)
		systemClassName += " scale-label-changed";

	let convertToMassClassName = "scale-label";
	if (props.convertToMass)
		convertToMassClassName += " scale-label-changed";

	let newScale = null;

	function showScaleDialog() {
		setIsScaleModalOpen(true);
	}

	function toggleSystem() {
		if (props.system !== "metric") {
			props.setSystem("metric");
		}
		else {
			props.setSystem(null);
		}
	}

	function toggleConvertToMass() {
		if (props.convertToMass !== "1") {
			props.setConvertToMass("1");
		}
		else {
			props.setConvertToMass(null);
		}
	}

	function onScaleEntryModalApply() {
		if (newScale)
			props.setScale(newScale);

		setIsScaleModalOpen(false);
	}

	function onScaleEntryModalCancel() {
		setIsScaleModalOpen(false);
	}

	function onScaleChange(value) {
		newScale = value;
		return newScale;
	}

	function handleImageClick(event) {
		setIsFullScreen(true);
		setFullScreenIndex(currentSlide);
		event.stopPropagation();
	}

	let scaleLabel = (<span className={`${scale === "1" ? "no-print" : ""} ${scaleClassName}`}>(<button className="link-button" onClick={showScaleDialog}>{`scale: x ${scale}`}</button>)</span>);
	let systemLabel = (<span className={`no-print ${systemClassName}`}>(<button className="link-button" onClick={toggleSystem}>{`metric: ${props.system === "metric" ? "on" : "off"}`}</button>)</span>);
	let convertToMassLabel = (<span className={`no-print ${convertToMassClassName}`}>(<button className="link-button" onClick={toggleConvertToMass}>{`mass: ${props.convertToMass ? "on" : "off"}`}</button>)</span>);

	const multipleImages = props.imageUrls.length > 1;
	const imageStyle = { width: imageSize, height: imageSize };

	return (
		<div className="rb-recipe-info">
			{props.imageUrls && props.imageUrls.length > 0 ?
				<div className="rb-recipe-images">
					<div style={{ display: 'flex', justifyContent: 'center' }}>
						<Slide
							ref={slideRef}
							className="slides"
							style={imageStyle}
							easing="ease"
							arrows={multipleImages}
							autoplay={multipleImages}
							canSwipe={multipleImages}
							onChange={(oldIndex, newIndex) => setCurrentSlide(newIndex)}
						>
							{props.imageUrls.map(imageUrl => (
								<div key={imageUrl} className="each-slide" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: imageSize, height: imageSize }}>
									<img alt="" src={`${imageUrl}/${imageSize}x${imageSize}`} onClick={handleImageClick} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
								</div>
							))}
						</Slide>
					</div>
					{multipleImages ? (
						<div className="rb-image-thumbs">
							{props.imageUrls.map((imageUrl, index) => (
								<button
									key={imageUrl}
									type="button"
									className={`rb-thumb ${currentSlide === index ? "active" : ""}`}
									onClick={() => {
										if (slideRef.current && typeof slideRef.current.goTo === "function") {
											slideRef.current.goTo(index);
										}
										setCurrentSlide(index);
									}}
								>
									<img alt="thumb" src={`${imageUrl}/100x100`} />
								</button>
							))}
						</div>
					) : null}
				</div>
				: <React.Fragment />
			}
			<div style={{ float: "left" }}>
				<Typography variant="h6" color="primary">
					{props.title} {scaleLabel} {systemLabel} {convertToMassLabel}
				</Typography>
				<Linkify properties={{ target: "_blank" }}>
					<Table className={"rb-recipe-info-body rb-recipe-ingredient-list" + (scale === "1" ? "" : " rb-scale-enabled")}>
						<tbody>
							{tableRows}
						</tbody>
					</Table>
				</Linkify>
			</div>
			<div style={{ clear: "both" }} />
			<Divider className="no-print" style={{ marginTop: 12 }} />
			<ScaleEntryModal
				isOpen={isScaleModalOpen}
				scale={scale}
				onApply={onScaleEntryModalApply}
				onCancel={onScaleEntryModalCancel}
				onScaleChange={onScaleChange} />
			{isFullScreen && (
				<div
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100vw',
						height: '100vh',
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						zIndex: 10000,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer'
					}}
					onClick={() => setIsFullScreen(false)}
				>
					<img
						alt=""
						src={`${props.imageUrls[fullScreenIndex]}/${Math.floor(Math.min(window.innerWidth * 0.9, window.innerHeight * 0.9))}x${Math.floor(Math.min(window.innerWidth * 0.9, window.innerHeight * 0.9))}`}
						style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
					/>
				</div>
			)}
		</div >
	);
}
